import React, { useState } from 'react';

interface MarkAttendanceModalProps {
  classId?: string;
  subject?: string;
}

const MarkAttendanceModal: React.FC<MarkAttendanceModalProps> = ({ classId, subject }) => {
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [period, setPeriod] = useState('1');
  
  // Mock student data
  const [students, setStudents] = useState([
    { id: '1', name: 'John Smith', rollNo: '001', status: 'present' },
    { id: '2', name: 'Sarah Johnson', rollNo: '002', status: 'present' },
    { id: '3', name: 'Mike Wilson', rollNo: '003', status: 'absent' },
    { id: '4', name: 'Emily Davis', rollNo: '004', status: 'present' },
    { id: '5', name: 'James Brown', rollNo: '005', status: 'late' }
  ]);

  const handleStatusChange = (studentId: string, status: string) => {
    setStudents(students.map(student => 
      student.id === studentId ? { ...student, status } : student
    ));
  };

  const handleMarkAll = (status: string) => {
    setStudents(students.map(student => ({ ...student, status })));
  };

  const handleSubmit = () => {
    // TODO: Submit attendance API call
    console.log('Submitting attendance:', {
      classId,
      subject,
      date: attendanceDate,
      period,
      attendance: students
    });
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'present': return 'btn-success';
      case 'absent': return 'btn-danger';
      case 'late': return 'btn-warning';
      default: return 'btn-outline-secondary';
    }
  };

  const getAttendanceStats = () => {
    const present = students.filter(s => s.status === 'present').length;
    const absent = students.filter(s => s.status === 'absent').length;
    const late = students.filter(s => s.status === 'late').length;
    return { present, absent, late, total: students.length };
  };

  const stats = getAttendanceStats();

  return (
    <div className="modal fade" id="markAttendanceModal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Mark Attendance</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div className="modal-body">
            {/* Class Info */}
            <div className="row mb-4">
              <div className="col-md-4">
                <label className="form-label">Date</label>
                <input 
                  type="date" 
                  className="form-control"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Period</label>
                <select 
                  className="form-select"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <option value="1">1st Period</option>
                  <option value="2">2nd Period</option>
                  <option value="3">3rd Period</option>
                  <option value="4">4th Period</option>
                  <option value="5">5th Period</option>
                  <option value="6">6th Period</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Subject</label>
                <input 
                  type="text" 
                  className="form-control"
                  value={subject || 'Mathematics'}
                  readOnly
                />
              </div>
            </div>

            {/* Stats */}
            <div className="row mb-3">
              <div className="col-md-3">
                <div className="card bg-success-transparent">
                  <div className="card-body text-center py-2">
                    <h5 className="text-success mb-0">{stats.present}</h5>
                    <small>Present</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-danger-transparent">
                  <div className="card-body text-center py-2">
                    <h5 className="text-danger mb-0">{stats.absent}</h5>
                    <small>Absent</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-warning-transparent">
                  <div className="card-body text-center py-2">
                    <h5 className="text-warning mb-0">{stats.late}</h5>
                    <small>Late</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-info-transparent">
                  <div className="card-body text-center py-2">
                    <h5 className="text-info mb-0">{stats.total}</h5>
                    <small>Total</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-3">
              <label className="form-label">Quick Actions:</label>
              <div className="btn-group" role="group">
                <button 
                  type="button" 
                  className="btn btn-outline-success btn-sm"
                  onClick={() => handleMarkAll('present')}
                >
                  Mark All Present
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleMarkAll('absent')}
                >
                  Mark All Absent
                </button>
              </div>
            </div>

            {/* Student List */}
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Roll No</th>
                    <th>Student Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>{student.rollNo}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-sm me-2">
                            <span className="avatar-title bg-primary-transparent text-primary">
                              {student.name.charAt(0)}
                            </span>
                          </div>
                          {student.name}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          student.status === 'present' ? 'badge-soft-success' :
                          student.status === 'absent' ? 'badge-soft-danger' :
                          'badge-soft-warning'
                        }`}>
                          {student.status.charAt(0).toUpperCase() + student.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button 
                            type="button" 
                            className={`btn ${student.status === 'present' ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => handleStatusChange(student.id, 'present')}
                          >
                            <i className="ti ti-check"></i>
                          </button>
                          <button 
                            type="button" 
                            className={`btn ${student.status === 'absent' ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => handleStatusChange(student.id, 'absent')}
                          >
                            <i className="ti ti-x"></i>
                          </button>
                          <button 
                            type="button" 
                            className={`btn ${student.status === 'late' ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => handleStatusChange(student.id, 'late')}
                          >
                            <i className="ti ti-clock"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
              <i className="ti ti-device-floppy me-2"></i>Save Attendance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkAttendanceModal;
